import { z } from 'zod';

import { DashboardWidgetWidth } from '@/components/dashboard/model';
import {
  dashboardDateScopeSchema,
  DashboardMetric,
  DashboardWidgetAction,
} from '../../../dashboard/model';

export enum NetworkDashboardWidgetType {
  SectionHeading = 'NETWORK_SECTION_HEADING',
  Metric = 'NETWORK_METRIC',
  PerformanceChart = 'NETWORK_PERFORMANCE_CHART',
  PerformanceTables = 'NETWORK_PERFORMANCE_TABLES',
  RevenueDistribution = 'NETWORK_REVENUE_DISTRIBUTION',
  WonBidsDistribution = 'NETWORK_WON_BIDS_DISTRIBUTION',
  DemandPerformance = 'NETWORK_DEMAND_PERFORMANCE',
}

export enum NetworkDashboardSection {
  DemandSources = 'DEMAND_SOURCES',
}

const base = {
  id: z.string().trim().min(1),
  type: z.enum(NetworkDashboardWidgetType),
  width: z.enum(DashboardWidgetWidth),
  dateScope: dashboardDateScopeSchema,
};

export const networkMetricWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.Metric),
  metric: z.enum(DashboardMetric),
});

export const networkSectionHeadingWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.SectionHeading),
  section: z.enum(NetworkDashboardSection),
});

export const networkPerformanceChartWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.PerformanceChart),
  metrics: z.array(z.enum(DashboardMetric)).min(1),
  actions: z.array(z.enum(DashboardWidgetAction)).default([]),
});

export const networkPerformanceTablesWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.PerformanceTables),
  pageSize: z.number().int().positive().max(100),
});

export const networkRevenueDistributionWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.RevenueDistribution),
});

export const networkWonBidsDistributionWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.WonBidsDistribution),
});

export const networkDemandPerformanceWidgetSchema = z.object({
  ...base,
  type: z.literal(NetworkDashboardWidgetType.DemandPerformance),
  pageSize: z.number().int().positive().max(100),
});

export const networkDashboardWidgetSchema = z.discriminatedUnion('type', [
  networkSectionHeadingWidgetSchema,
  networkMetricWidgetSchema,
  networkPerformanceChartWidgetSchema,
  networkPerformanceTablesWidgetSchema,
  networkRevenueDistributionWidgetSchema,
  networkWonBidsDistributionWidgetSchema,
  networkDemandPerformanceWidgetSchema,
]);

export const networkDashboardDocumentSchema = z.object({
  version: z.literal(1),
  widgets: z.array(networkDashboardWidgetSchema),
});

export type NetworkDashboardWidget = z.infer<typeof networkDashboardWidgetSchema>;
export type NetworkMetricWidget = z.infer<typeof networkMetricWidgetSchema>;
export type NetworkPerformanceChartWidget = z.infer<typeof networkPerformanceChartWidgetSchema>;
export type NetworkPerformanceTablesWidget = z.infer<typeof networkPerformanceTablesWidgetSchema>;
export type NetworkDemandPerformanceWidget = z.infer<typeof networkDemandPerformanceWidgetSchema>;
